import React, { useEffect } from 'react'
import { useSetState } from 'react-use'
import ReactCrop from 'react-image-crop'
import PropTypes from 'prop-types'
import { withTranslationCustom } from 'lib/i18next'

import Button from 'components/UI/Button'

import 'react-image-crop/dist/ReactCrop.css'
import './image-crop.pcss'

const ImageCrop = ({
    image,
    onCancelCropping,
    onCrop,
    isBlob = false,
    originalImage,
    previewName,
    hideCancelButton = false,
    t
}) => {
    const [cropState, setCropState] = useSetState({
        src: null,
        crop: {
            unit: '%',
            width: 100,
            height: 100,
        },
        imageRef: null,
        croppedImageUrl: null,
        showPreview: false,
    })

    useEffect(() => {
        if (!isBlob) {
            setCropState({ src: originalImage || image.large })
        } else {
            setCropState({ src: image })
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const onImageLoaded = image => {
        const scaleX = image.naturalWidth / image.width
        const scaleY = image.naturalHeight / image.height

        setCropState({
            imageRef: image,
            scaleX: scaleX,
            scaleY: scaleY,
        })
    }

    const onCropComplete = crop => {
        makeClientCrop(crop)
    }

    const onCropChange = (crop, percentCrop) => {
        // You could also use percentCrop:
        // this.setState({ crop: percentCrop })
        setCropState({ crop: crop })
    }

    const makeClientCrop = async (crop) => {
        if (cropState.imageRef && crop.width && crop.height) {
            const croppedImageUrl = await getCroppedImg(
                cropState.imageRef,
                crop,
                'newFile.jpeg'
            )
            setCropState({ croppedImageUrl: croppedImageUrl })
        }
    }

    const getCroppedImg = (image, crop, fileName) => {
        const canvas = document.createElement('canvas')
        const scaleX = image.naturalWidth / image.width
        const scaleY = image.naturalHeight / image.height
        canvas.width = crop.width
        canvas.height = crop.height
        const ctx = canvas.getContext('2d')

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
        )

        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (!blob) {
                    // reject(new Error('Canvas is empty'))
                    console.error('Canvas is empty')
                    return
                }
                blob.name = fileName
                const fileUrl = window.URL.createObjectURL(blob)
                resolve(fileUrl)
            }, 'image/jpeg')
        })
    }

    const actions = () => {
        return (
            <>
                <Button
                    className="button image-crop__done"
                    trackName="cancel"
                    onClick={() => {
                        const { crop, scaleX, scaleY } = cropState
                        const cropData = {
                            x: Math.floor(crop.x * scaleX),
                            y: Math.floor(crop.y * scaleY),
                            width: Math.floor(crop.width * scaleX),
                            height: Math.floor(crop.height * scaleY)
                        }
                        onCrop(image.id, cropData)
                    }}
                >
                    {t('common:save')}
                </Button>
                {!hideCancelButton && (
                    <Button
                        className="button button--secondary image-crop__cancel"
                        trackName="cancel"
                        onClick={() => onCancelCropping()}
                    >
                        {t('common:cancel')}
                    </Button>
                )}
            </>
        )
    }

    const previewActions = () => {
        return (
            <div className="image-crop__preview-button-container">
                {cropState.croppedImageUrl && (
                    <Button
                        className="image-crop__preview-button button button--tertiary"
                        trackName="cropPreview"
                        onClick={() => setCropState(state => {
                            return { showPreview: !state.showPreview }
                        })}
                    >
                        <span>{cropState.showPreview ? t('common:backToCrop') : t('common:preview')}</span>
                    </Button>
                )}
            </div>
        )
    }

    return (
        <div className="image-crop">
            {cropState.src && (
                <>
                    {!cropState.showPreview && (
                        <div className="image-crop__tip">{t('vehicleProfile:pictures.cropTip')}</div>
                    )}
                    <div
                        className="image-crop__cropper"
                        style={{
                            visibility: cropState.showPreview ? 'hidden' : 'visible',
                            height: cropState.showPreview ? '0' : 'auto'
                        }}
                    >
                        <ReactCrop
                            src={cropState.src}
                            crop={cropState.crop}
                            ruleOfThirds
                            onImageLoaded={onImageLoaded}
                            onComplete={onCropComplete}
                            onChange={onCropChange}
                            imageStyle={{ width: 'auto', height: 'auto', maxHeight: '600px' }}
                            keepSelection={true}
                            crossorigin="anonymous"
                        />
                    </div>
                </>
            )}
            {cropState.showPreview && (
                <div className="image-crop__preview">
                    <div className="image-crop__preview-profile">
                        <div
                            className="image-crop__preview-profile-image"
                            style={{ backgroundImage: `url(${cropState.croppedImageUrl})` }}
                        />
                        <div className="image-crop__preview-profile-vehicle-name">{previewName}</div>
                    </div>
                    <div className="image-crop__preview-card">
                        <div
                            className="image-crop__preview-card-image"
                            style={{ backgroundImage: `url(${cropState.croppedImageUrl})` }}
                        />
                        <h2 className="image-crop__preview-card-vehicle-name">{previewName}</h2>
                    </div>
                </div>
            )}
            {previewActions()}
            <div className="image-crop__actions">
                {actions()}
            </div>
        </div>
    )
}

ImageCrop.propTypes = {
    image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    onCancelCropping: PropTypes.func,
    onCrop: PropTypes.func,
    isBlob: PropTypes.bool,
    originalImage: PropTypes.string,
    previewName: PropTypes.string,
    hideCancelButton: PropTypes.bool,
}

export default withTranslationCustom(['vehicleProfile'])(ImageCrop)
